import type {ComponentProps} from '@enonic/react-components'
import * as React from 'react'
import {
    Section,
    Container,
    FilterBar,
    Pagination,
    NewsItemCard,
    EducationCard,
    PersonCard,
} from '../primitives'
import type {
    FilterItem,
    PaginationData,
    NewsItemCardProps,
    EducationCardProps,
    PersonCardProps,
} from '../primitives'

export interface ContentListData extends Record<string, unknown> {
    heading?: string
    variant: 'news' | 'education' | 'person'
    items: unknown[]
    allItems: unknown[]
    itemsPerPage: number
    categories: FilterItem[]
    activeFilter?: string
    paramName: string
    allLabel: string
    anchor?: string
    emptyMessage: string
    pagination: PaginationData
}

interface ItemWithCategory {
    categoryValue?: string
}

function renderCard(variant: ContentListData['variant'], item: unknown): React.ReactElement | null {
    if (variant === 'news') {
        const n = item as NewsItemCardProps
        return (
            <NewsItemCard
                key={n.id}
                {...n}
                headingLevel="h2"
                className="col-span-full md:col-span-6"
            />
        )
    }
    if (variant === 'education') {
        const e = item as EducationCardProps
        return (
            <EducationCard
                key={e.url}
                {...e}
                headingLevel="h2"
                className="col-span-full md:col-span-4"
            />
        )
    }
    if (variant === 'person') {
        const p = item as PersonCardProps
        return (
            <PersonCard
                key={p.id}
                {...p}
                headingLevel="h2"
                className="col-span-full md:col-span-4"
            />
        )
    }
    return null
}

function buildQuery(paramName: string, filter: string | undefined, page: number): string {
    const parts: string[] = []
    if (filter) parts.push(paramName + '=' + encodeURIComponent(filter))
    if (page > 1) parts.push('side=' + page)
    return parts.length > 0 ? '?' + parts.join('&') : ''
}

export const ContentList = (props: ComponentProps) => {
    const data = props.data as ContentListData
    const {heading, variant, allItems, itemsPerPage, categories, paramName, allLabel, anchor, emptyMessage} = data

    const [activeFilter, setActiveFilter] = React.useState<string | undefined>(data.activeFilter)
    const [currentPage, setCurrentPage] = React.useState<number>(data.pagination.currentPage)

    const filteredItems = React.useMemo<unknown[]>(() => {
        if (!activeFilter) return allItems
        return allItems.filter((item) => (item as ItemWithCategory).categoryValue === activeFilter)
    }, [allItems, activeFilter])

    const totalPages = Math.max(1, Math.ceil(filteredItems.length / itemsPerPage))
    const safePage = Math.min(currentPage, totalPages)
    const start = (safePage - 1) * itemsPerPage
    const pageItems = filteredItems.slice(start, start + itemsPerPage)

    const pagination: PaginationData = {
        currentPage: safePage,
        totalPages,
        hasPrev: safePage > 1,
        hasNext: safePage < totalPages,
    }

    React.useEffect(() => {
        if (typeof window === 'undefined') return
        const desired = window.location.pathname + buildQuery(paramName, activeFilter, safePage)
        const current = window.location.pathname + window.location.search
        if (current !== desired) {
            window.history.replaceState(null, '', desired + window.location.hash)
        }
    }, [paramName, activeFilter, safePage])

    const handleFilterChange = (value: string | undefined) => {
        setActiveFilter(value)
        setCurrentPage(1)
    }

    const handlePageChange = (page: number) => {
        setCurrentPage(page)
    }

    const anchorId = anchor ? anchor.replace('#', '') : undefined

    return (
        <>
            <Section id={anchorId}>
                <Container className="gap-y-8 md:gap-y-12">
                    {heading && (
                        <h2 className="col-span-full text-5xl text-blue-900">
                            {heading}
                        </h2>
                    )}
                    {categories.length > 0 && (
                        <FilterBar
                            items={categories}
                            allLabel={allLabel}
                            paramName={paramName}
                            ariaLabel="Filtrer"
                            activeValue={activeFilter}
                            onChange={handleFilterChange}
                        />
                    )}
                    {pageItems.length > 0 ? (
                        pageItems.map((item) => renderCard(variant, item))
                    ) : (
                        <p className="col-span-full text-lg text-slate-900">
                            {emptyMessage}
                        </p>
                    )}
                </Container>
            </Section>
            <Pagination
                pagination={pagination}
                activeFilter={activeFilter}
                paramName={paramName}
                onPageChange={handlePageChange}
            />
        </>
    )
}
